import { List } from "immutable";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper, Utility } from "../../../utils";
import { TagEditModal as TagEditModal, TagMergeModal } from "../components";

type DataRow = M.Tag.TagAdmin & {
};

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    tags: state.adminTag.tags.data,
    tagsObj: state.adminTag.tags,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadTags: () => dispatch(Actions.AdminTag.loadTags()),
    removeTag: (tagId: number) => dispatch(Actions.AdminTag.removeTag(tagId)),
    toggleTagsLoading: (loading: boolean) => dispatch(Actions.AdminTag.toggleLoadingTags(loading)),
    updateTag: (tag: M.Tag.TagAdmin) => dispatch(Actions.AdminTag.updateTag(tag)),
});

type OwnProps = RouteComponentProps<{}> & {
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    tags: List<M.Tag.TagAdmin>;
    tagsObj: M.Generic.StoreDataObject<List<M.Tag.TagAdmin>>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadTags: () => void;
    removeTag: (tagId: number) => void;
    toggleTagsLoading: (loading: boolean) => void;
    updateTag: (tag: M.Tag.TagAdmin) => void;
};

interface OwnState {
    tags: List<DataRow>;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ListComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private _editModal: TagEditModal | null = null;
    private _mergeModal: TagMergeModal | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
            ...this.getInitialListViewState(props),
            tags: List(),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (prevProps.tagsObj.loadedOn !== this.props.tagsObj.loadedOn)
            this.loadTagsFromState();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const loading = this.state.loading || !!this.props.tagsObj.loading;

        const actionsPrimary = <BS.ButtonToolbar>
            <BS.ButtonGroup>
                <BS.Button color="default" onClick={() => this.showEditModal()}>
                    <Comp.Generic.Icon icon="plus" /> Add tag
                </BS.Button>
            </BS.ButtonGroup>
        </BS.ButtonToolbar>;

        const tableActions = this.getTableActions({
            dataLength: this.state.tags.size,
            filteredLength: this.state.tags.size,
            lengthMenu: [10, 25, 100],
            searchPlaceholder: "Search tags...",
        });

        return <Comp.Page.AdminPage actionsPrimary={actionsPrimary} actionsSecondary={tableActions} title={"Tags"}>

            <Comp.Modal.ConfirmModal
                headerClassName="warning"
                onProceed={() => this.deleteTag()}
                title="Confirm delete"
                renderBody={(data?: M.Tag.TagAdmin) => {
                    const count = data && data.usageCount
                        ? <>It has {data.usageCount} associated entit{data.usageCount === 1 ? "y" : "ies"}</>
                        : null;

                    return <p>Are you sure you want to delete this tag? {count}</p>;
                }}
                ref={(ref) => this._confirmDelete = ref}
            />

            <TagEditModal
                api={this.props.api}
                onComplete={(tag) => this.onTagSaved(tag)}
                ref={(ref) => this._editModal = ref}
            />

            <TagMergeModal
                api={this.props.api}
                onComplete={(fromId, toTag) => this.onTagMerged(fromId, toTag)}
                ref={(ref) => this._mergeModal = ref}
            />

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            <BT.BootstrapTable
                bordered={false}
                data={this.state.tags.toArray()}
                pagination={this.state.tablePageSize > -1}
                options={{
                    afterSearch: (searchText, results) => this.onTableSearch(searchText, results),
                    page: this.state.tablePage,
                    paginationPanel: () => <div />,
                    sizePerPage: this.state.tablePageSize,
                    onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                    defaultSortName: "name",
                    defaultSortOrder: "asc",
                }}
                ref={(ref: any) => this.table = ref}
            >
                <BT.TableHeaderColumn dataField="id" hidden isKey width="5%">ID</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="name"
                    dataFormat={(cell: any, row: DataRow) => (
                        <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.showEditModal(row); }} title="Edit">
                            {row.name}
                        </a>)}
                    dataSort
                >Name</BT.TableHeaderColumn>

                <BT.TableHeaderColumn dataField="usageCount" dataSort width="10%">Usage count</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="id"
                    dataFormat={(cell: any, row: DataRow) => (
                        <>
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.showMergeModal(row); }} title="Merge">
                                <Comp.Generic.Icon icon="code-fork fa-lg" />
                            </a>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.showDeleteConfirmation(row); }} title="Delete">
                                <Comp.Generic.Icon icon="times fa-lg" />
                            </a>
                        </>
                    )}
                    dataSort
                    width="75px"
                />
            </BT.BootstrapTable>

        </Comp.Page.AdminPage>;
    }

    private async deleteTag() {
        if (this._confirmDelete === null)
            return;

        const tag = this._confirmDelete.getRef() as M.Tag.TagAdmin;
        if (!tag)
            return;

        const id = tag.id ;
        const result = await this.props.api.delete<boolean>(Api.endpoints.admin.tag.tag.withUrlParams({ id }));

        if (!this._isMounted)
            return;

        if (result.isSuccess) {
            this.props.removeTag(tag.id);
            this.props.notifySuccess(`Tag <strong>${tag.name}</strong> has been successfully deleted.`);
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }

        if (this._confirmDelete !== null)
            this._confirmDelete.close();
    }

    private async initializeComponent() {
        await this.loadTagsFromState(true);
        this.props.loadTags();
    }

    private async loadTagsFromState(toggleLoading: boolean = false) {
        const tags: List<DataRow> = this.props.tags;
        await this.setStateAsync({ ...this.state, tags });

        if (!tags.size && toggleLoading)
            this.props.toggleTagsLoading(true);

        // update table state from location state if available
        this.updateTableState(tags);
    }

    private async onTagMerged(fromId: number, toTag: M.Tag.TagAdmin) {
        this.props.notifySuccess("Tag has been successfully merged.");
        this.props.removeTag(fromId);
        await Utility.delay(150);
        this.props.updateTag(toTag);
    }

    private async onTagSaved(tag: M.Tag.TagAdmin) {
        this.props.notifySuccess("Tag has been successfully saved.");
        this.props.updateTag(tag);
    }

    private showEditModal(tag?: M.Tag.TagAdmin) {
        if (!this._editModal)
            return;

        this._editModal.show(tag);
    }

    private showMergeModal(tag: M.Tag.TagAdmin) {
        if (!this._mergeModal)
            return;

        this._mergeModal.show(tag);
    }

    private showDeleteConfirmation(tag: M.Tag.TagAdmin) {
        if (this._confirmDelete !== null)
            this._confirmDelete.show(tag);
    }
}

export const ListTags: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ListComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminTag/list/List.tsx